import { CompanyPanel } from "../components/Structure";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { gbp, multiple, pct, quarterOf, ukDate } from "../lib/format";
import { ErrorState, Loading, Panel, PotBadge, StatusBadge } from "../components/common";
import { QuarterGrid } from "../components/QuarterGrid";
import { EditProjectDialog } from "../components/EditProjectDialog";
import { ChecksList, HistoryList } from "../components/ProjectBits";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ProjectDocs, ProjectDrawdowns } from "../components/ProjectDocs";
import { ReceiptButton } from "../components/ReceiptDialog";
import { ExitPanel } from "../components/ExitPanel";
import { PaymentLogPanel } from "../components/PaymentLog";
import { ProjectRecord } from "../components/ProjectRecord";
import { useState } from "react";

function Stat({ label, value, testid, tone }) {
  return (
    <div className="rounded-lg border bg-card p-4" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div key={String(value)} className={`flash mt-2 font-figure text-xl font-bold ${tone || ""}`} data-testid={`${testid}-value`}>{value}</div>
    </div>
  );
}

function ProformaBlock({ p }) {
  const m = p.metrics;
  const rows = [
    ["Equity required", gbp(p.proforma?.equity), gbp(m.current_equity), gbp(m.equity_change), "pf-equity"],
    ["EPIL equity return date", ukDate(p.proforma?.equity_return_date), ukDate(p.projected?.equity_return_date), m.last_return_quarter ? `cashflow ${m.last_return_quarter}` : "–", "pf-return-date"],
    ["EPIL profit", gbp(p.proforma?.profit), gbp(m.profit), gbp(m.profit_change), "pf-profit"],
    ["Latest projected profit", "", gbp(p.projected?.profit), "", "pf-projected-profit"],
    ["EPIL profit date", ukDate(p.proforma?.profit_date), ukDate(p.projected?.profit_date), m.last_profit_quarter ? `cashflow ${m.last_profit_quarter}` : "–", "pf-profit-date"],
  ];
  return (
    <table className="w-full text-sm" data-testid="proforma-block">
      <thead><tr className="border-b text-xs text-muted-foreground text-left">
        <th className="py-2 pr-3"></th><th className="py-2 px-3 text-right">Proforma</th><th className="py-2 px-3 text-right">Current / projected</th><th className="py-2 pl-3 text-right">Change / cashflow</th>
      </tr></thead>
      <tbody>{rows.map(([l, a, b, c, id]) => (
        <tr key={id} className="border-b last:border-0" data-testid={id}>
          <td className="py-2.5 pr-3 font-medium">{l}</td>
          <td className="py-2.5 px-3 text-right font-figure" data-testid={`${id}-proforma`}>{a}</td>
          <td className="py-2.5 px-3 text-right font-figure font-semibold" data-testid={`${id}-current`}>{b}</td>
          <td className="py-2.5 pl-3 text-right font-figure text-xs text-muted-foreground">{c}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function SplitBlock({ s }) {
  const other = s.pot === "AMSM" ? "Shabeer (SM)" : "Other parties";
  const rows = [
    ["Afzal (AM)", s.am_equity, s.am_profit_kept, pct(s.am_equity_pct, 2)],
    ["Family Office", 0, s.fo_profit, pct(s.am_profit_pct * s.fo_pct, 2) + " of profit"],
    [other, s.pot === "AMSM" ? s.sm_equity : s.other_equity, s.pot === "AMSM" ? s.sm_profit : s.other_profit, pct(1 - s.am_equity_pct, 2)],
  ];
  return (
    <table className="w-full text-sm" data-testid="owner-split-block">
      <thead><tr className="border-b text-xs text-muted-foreground text-left"><th className="py-2">Who</th><th className="py-2 text-right">Equity</th><th className="py-2 text-right">Profit</th><th className="py-2 text-right">Share</th></tr></thead>
      <tbody>{rows.map(([w, e, p, sh]) => (
        <tr key={w} className="border-b last:border-0" data-testid={`split-${w.split(" ")[0].toLowerCase()}`}>
          <td className="py-2.5 font-medium">{w}</td><td className="py-2.5 text-right font-figure">{gbp(e)}</td>
          <td className="py-2.5 text-right font-figure">{gbp(p)}</td><td className="py-2.5 text-right text-xs text-muted-foreground">{sh}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { isPartner, user } = useAuth();
  const { data: p, error, status, loading, setData, reload } = useApi(`/projects/${id}`);
  const history = useApi(`/projects/${id}/history`);
  const [reason, setReason] = useState("");
  if (loading && !p) return <Loading />;
  if (error && !p) return <ErrorState message={error} status={status} />;
  const m = p.metrics;
  const saveCell = async (period, field, value) => {
    if (!reason.trim()) { toast.error("Please give a reason for the forecast change first"); return; }
    try {
      const { data } = await api.put(`/projects/${id}/cashflow`, { changes: [{ period, field, value }], reason });
      setData(data); history.reload(true); toast.success("Saved – new forecast version");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="project-detail-page">
      <Link to={isPartner ? "/projects" : "/projects"} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-projects"><ArrowLeft className="h-4 w-4" /> Projects</Link>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between rise">
        <div className="max-w-3xl">
          <div className="eyebrow mb-2" data-testid="project-partner">{p.partner_name}{p.partner_on_tab && p.partner_on_tab !== p.partner_name ? ` · ${p.partner_on_tab}` : ""}</div>
          <h1 className="text-2xl md:text-4xl font-bold tracking-tight" data-testid="project-name">{p.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <StatusBadge status={p.status} /> {m.pot && <PotBadge pot={m.pot} />}
            {m.pot && <span className="rounded border px-1.5 py-0.5 text-xs font-semibold" data-testid="project-owner-code">{p.owner_code}</span>}
            {!isPartner && p.epil_share && <span className="rounded border px-1.5 py-0.5 text-xs font-semibold" data-testid="project-pot-share">{p.epil_share.label} {p.epil_share.pct == null ? "Not set" : `${Math.round(p.epil_share.pct * 1000) / 10}%`}</span>}
            <span className="text-muted-foreground">SPV: <span className="text-foreground" data-testid="project-spv">{p.spv || "–"}</span></span>
            <span className="text-muted-foreground">· Proforma date <span className="text-foreground font-semibold" data-testid="project-proforma-signed">{ukDate(p.proforma_signed)}</span></span>
            <span className="text-muted-foreground">· Report date {ukDate(p.report_date)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">{!isPartner && user.roles.includes("Finance") && <ReceiptButton projectId={id} onDone={() => { reload(true); history.reload(true); }} />}
        {p.can_edit && <EditProjectDialog project={p} onSaved={(d) => { setData(d); history.reload(true); }} />}</div>
      </div>
      <ProjectRecord id={id} />
      {!isPartner && <CompanyPanel kind="projects" id={id} title="SPV, bank and set-up" />}
      <PaymentLogPanel projectId={id} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Equity requirement (proforma)" value={gbp(p.proforma?.equity)} testid="stat-proforma-equity" />
        <Stat label="EPIL profit (proforma)" value={gbp(p.proforma?.profit)} testid="stat-proforma-profit" />
        <Stat label="Equity deployed" value={gbp(m.deployed)} testid="stat-deployed" />
        <Stat label="Equity to deploy" value={gbp(m.to_deploy)} testid="stat-to-deploy" />
        <Stat label="Current IRR" value={pct(m.irr_current)} testid="stat-irr-current" />
        <Stat label="Proforma IRR" value={pct(m.irr_proforma)} testid="stat-irr-proforma" />
        <Stat label="Money multiple" value={multiple(m.money_multiple)} testid="stat-money-multiple" />
        <Stat label="Profit on equity" value={`${pct(m.profit_on_equity)} · pf ${pct(m.profit_on_equity_proforma)}`} testid="stat-profit-on-equity" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Panel eyebrow="Proforma vs current" title="As signed and as it stands"><ProformaBlock p={p} /></Panel>
        <Panel eyebrow="Totals from the quarterly rows" title="Cashflow totals" testid="cashflow-totals">
          <dl className="grid grid-cols-2 gap-y-3 text-sm">
            {[["Equity deployed", gbp(m.deployed)], ["Equity to deploy", gbp(m.to_deploy)], ["EPIL equity return", gbp(m.equity_return)], ["EPIL profit", gbp(m.profit)], ["Net cash flow", gbp(m.net)],
              ["Last equity return quarter", m.last_return_quarter || "–"], ["Last profit quarter", m.last_profit_quarter || "–"], ["Projected profit quarter", quarterOf(p.projected?.profit_date)]].map(([l, v]) => (
              <div key={l} className="contents"><dt className="text-muted-foreground">{l}</dt><dd className="text-right font-figure font-semibold">{v}</dd></div>
            ))}
          </dl>
        </Panel>
      </div>

      <Panel eyebrow="Quarterly cashflow" title="Cashflow grid · Q1 2016 – Q4 2031" testid="cashflow-panel">
        {p.can_edit && <div className="mb-3 flex flex-wrap items-center gap-2 text-sm"><span className="text-muted-foreground">Reason for changes (required – each save creates a new forecast version):</span>
          <input className="h-9 min-w-[280px] flex-1 rounded-md border bg-background px-3" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Partner Q3 update – sales slipped" data-testid="cashflow-reason-input" /></div>}
        <QuarterGrid grid={p.grid} canEdit={p.can_edit} window={p.report_window} onSave={saveCell} />
      </Panel>

      <Tabs defaultValue={m.split ? "split" : m.checks ? "checks" : "history"}>
        <TabsList className="flex-wrap h-auto">
          {m.split && <TabsTrigger value="split" data-testid="tab-split">Who gets what</TabsTrigger>}
          {m.checks && <TabsTrigger value="checks" data-testid="tab-checks">Checks {m.flags > 0 && <span className="ml-1.5 rounded-full bg-primary px-1.5 text-[10px] text-primary-foreground">{m.flags}</span>}</TabsTrigger>}
          <TabsTrigger value="drawdowns" data-testid="tab-drawdowns">Drawdowns</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          {["Live", "Exited"].includes(p.status) && <TabsTrigger value="exit" data-testid="tab-exit">Exit</TabsTrigger>}
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>
        {m.split && <TabsContent value="split"><Panel title={`Owner code ${p.owner_code} · ${m.pot} pot`}><SplitBlock s={m.split} /></Panel></TabsContent>}
        {m.checks && <TabsContent value="checks"><Panel title="Checks"><ChecksList checks={m.checks} /></Panel></TabsContent>}
        <TabsContent value="drawdowns"><div className="mb-3 flex justify-end"><Link to={`/drawdowns/new?project=${id}`} className="text-sm font-semibold text-primary" data-testid="project-new-drawdown-link">+ New drawdown</Link></div><ProjectDrawdowns projectId={id} /></TabsContent>
        <TabsContent value="documents"><ProjectDocs projectId={id} /></TabsContent>
        {["Live", "Exited"].includes(p.status) && <TabsContent value="exit"><ExitPanel projectId={id} /></TabsContent>}
        <TabsContent value="history"><Panel title="Change history"><HistoryList items={history.data} /></Panel></TabsContent>
        <TabsContent value="notes"><Panel title="Notes">
          {p.notes && <p className="mb-4 text-sm">{p.notes}</p>}
          <ul className="space-y-1.5 text-sm text-muted-foreground" data-testid="project-comments">{(p.comments || []).map((c, i) => <li key={i}>• {c}</li>)}</ul>
          {!p.notes && !p.comments?.length && <p className="text-sm text-muted-foreground">No notes.</p>}
        </Panel></TabsContent>
      </Tabs>
    </div>
  );
}
